/**
 * Updated for new UI design (v2)
 * Changes made:
 * 1. Removed all code related to `ZippedCodeBlock`.
 * 2. Removed `tech` and `pro` code related to modifier codes.
 */

import React from 'react';
import classnames from 'classnames';
import { cmxTypeguards } from '@codametrix/ui-common';
import { TransactionHeader } from './transaction-headerV2';
import { TransactionBuildBody } from './transaction-bodyV2';

/**
 * Method that builds an accordion element
 * @param handleUnitsChange
 * @param handleTransactionReorder
 * @param handleCodeDelete
 * @param handleEnableTechPro
 * @param handleDisableTechPro
 * @param onFeatureSelection
 * @param panelActivations
 * @param medicalCode
 * @param idx
 * @param errors
 * @returns
 */
export const transactionAccordianBuilderV2 = (
  handleUnitsChange: (units: number, unitElement: HTMLElement) => void,
  handleTransactionReorder: (
    transactionCurrentIndex: number,
    transactionDestinationIndex: number
  ) => void,
  handleCodeDelete: (ev: any) => void,
  handleCodeChecked: (ev: any) => void,
  handleEnableTechPro: (
    techTransactionCodable: CMxCommonApp.TechCodableTransaction
  ) => void,
  handleDisableTechPro: (
    techTransactionCodable: CMxCommonApp.TechCodableTransaction
  ) => void,
  isTimeBasedCode: () => boolean,
  timeBasedCodePopup: () => React.ReactElement,
  onSearch: (codeQuery: CMxCommonApp.CodeQuery) => void,
  onSearchSelection: (codeQuery: Accelerate.CodeActionPayload) => void,
  onAddAllCodes: (
    codes: CMxCommonApp.CodeInfo[] | CMxCommonApp.SuggestedCode[]
  ) => void,
  onRemoveAllCodes: (
    codes: CMxCommonApp.CodeInfo[] | CMxCommonApp.SuggestedCode[]
  ) => void,
  codeDropHandler: (codes: CMxCommonApp.ChargeSessionCode[]) => void,
  onDropHandler: (
    dropAction: CMxCommonApp.DroppedAction<CMxCommonApp.ChargeSessionCode>
  ) => void,
  onAccordionAction: (action: any, item: any) => void,
  onDelete: (ev: any) => void,
  suggestedCodes: CMxCommonApp.SuggestedCodes,
  searchResults: Accelerate.CodeSearchResult,
  invoice: CMxCommonApp.CodableInvoice,
  codeEdits: CMxCommonApp.CodeEditInputs,
  editable: boolean,
  autoFocus: boolean,
  transactionIndex: number,
  codableTransaction: CMxCommonApp.CodableTransaction,
  transaction: CMxCommonApp.SimplifiedTransaction,
  errors: AmplifyAPI.AmplifyError[] = [],
  items: any
) => {
  let styleClasses = '';
  let techTransaction: CMxCommonApp.TechTransaction = {
    proCode: null,
    techCode: null,
    hasProCode: false,
    hasTechCode: false,
    isProCodeActive: false,
    isTechCodeActive: false,
    proContext: null,
    techContext: null,
    isModifier: false,
    isLoading: false
  };
  techTransaction.isLoading = invoice?.isLoadingTechPro ?? false;

  styleClasses = 'transaction-section';

  // Removing procedures with provenance TECH. Shallow-clone whenever we have a transaction —
  // `codableTransaction` may be frozen Immer/Redux state; never assign to `.codes` in place
  // (see V1 `transaction.tsx`) and do not fall through to the raw object when `codes` is
  // non-own (e.g. some proxies), which previously left frozen objects in the tree.
  let codableTransactionView: CMxCommonApp.CodableTransaction | undefined;
  if (codableTransaction == null) {
    codableTransactionView = codableTransaction;
  } else {
    const sourceCodes = codableTransaction.codes;
    const codes =
      sourceCodes == null
        ? sourceCodes
        : sourceCodes
            .filter(
              (code): code is CMxCommonApp.ChargeSessionCode =>
                cmxTypeguards.isChargeSessionCode(code)
            )
            .filter(
              (code) =>
                !(
                  code.codeType === 'procedure' && code.provenance === 'TECH'
                )
            );
    codableTransactionView = {
      ...codableTransaction,
      codes
    } as CMxCommonApp.CodableTransaction;
  }

  // assign tech/pro code if it exists
  const procedureCode = [codableTransactionView?.codes, transaction?.codes]
    .flatMap(codes => codes || [])
    .find(
      (code): code is CMxCommonApp.ChargeSessionCode =>
        cmxTypeguards.isChargeSessionCode(code) &&
        (code as CMxCommonApp.ChargeSessionCode).codeType === 'procedure'
    );

  if (codableTransactionView && procedureCode) {
    techTransaction.isModifier = false;
    // add tech/pro for procedure
    techTransaction.proCode =
      codableTransactionView?.mapped_procedure?.procedure_code ?? null;
    techTransaction.techCode =
      codableTransactionView?.technical_code?.code ?? null;

    techTransaction.hasProCode = !!codableTransactionView?.mapped_procedure
      ?.procedure_code;
    techTransaction.hasTechCode = !!codableTransactionView?.technical_code
      ?.code;

    techTransaction.isProCodeActive = techTransaction.hasProCode
      ? codableTransactionView?.mapped_procedure?.active
      : false;
    techTransaction.isTechCodeActive = techTransaction.hasTechCode
      ? codableTransactionView?.technical_code?.active
      : false;

    techTransaction.proContext = {
      transactionGuid: codableTransactionView?.guid,
      mappedProcedure: codableTransactionView?.mapped_procedure || null
    };
    techTransaction.techContext = {
      transactionGuid: codableTransactionView?.guid,
      technicalCode: codableTransactionView?.technical_code || null
    };
  }

  return {
    className: classnames(styleClasses),
    header: TransactionHeader(
      procedureCode,
      techTransaction,
      errors,
      invoice,
      codeEdits,
      handleUnitsChange,
      handleTransactionReorder,
      handleCodeDelete,
      handleCodeChecked,
      handleEnableTechPro,
      handleDisableTechPro,
      codeDropHandler,
      isTimeBasedCode,
      timeBasedCodePopup,
      onSearch,
      onSearchSelection,
      onAddAllCodes,
      onRemoveAllCodes,
      searchResults,
      codableTransactionView,
      transaction,
      editable,
      suggestedCodes,
      autoFocus,
      transactionIndex
    ),
    id: 0,
    body: TransactionBuildBody(
      codableTransactionView,
      onDropHandler,
      onAccordionAction,
      items,
      onDelete,
      editable
    )
  };
};
